import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Pencil, Trash2, AlertCircle, Eye, Wallet, Printer, X, Search, Users, Receipt, IndianRupee, Download,
} from 'lucide-react';
import { adminApi, formatApiError } from '../../utils/adminApi';
import {
  fmtINR, fmtInvoiceNumber, fmtDate, todayISO, dueDefaultISO, PAYMENT_MODES, STATUS_LABEL,
} from '../../utils/billingFormat';
import { exportInvoicePdf } from '../../utils/invoicePdf';

const SUBTABS = [
  { id: 'dashboard', label: 'Dashboard', icon: IndianRupee },
  { id: 'invoices', label: 'Invoices', icon: Receipt },
  { id: 'customers', label: 'Customers', icon: Users },
];

const BillingTab = ({ onToast }) => {
  const [sub, setSub] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // modal state
  const [editingInvoice, setEditingInvoice] = useState(null); // null|{}|{...}
  const [viewInvoice, setViewInvoice] = useState(null);
  const [payInvoice, setPayInvoice] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const [s, inv, cust] = await Promise.all([
        adminApi.billingStats(),
        adminApi.listInvoices(),
        adminApi.listBillingCustomers(),
      ]);
      setStats(s);
      setInvoices(inv);
      setCustomers(cust);
    } catch (err) {
      setError(formatApiError(err, 'Could not load billing data.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const handleDeleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${fmtInvoiceNumber(inv.number)}? This cannot be undone.`)) return;
    try {
      await adminApi.deleteInvoice(inv.id);
      onToast?.('Invoice deleted');
      refresh();
    } catch (err) { setError(formatApiError(err)); }
  };

  const handleDeleteCustomer = async (c) => {
    if (!window.confirm(`Remove customer "${c.name}"?`)) return;
    try {
      await adminApi.deleteBillingCustomer(c.id);
      onToast?.('Customer removed');
      refresh();
    } catch (err) { setError(formatApiError(err)); }
  };

  return (
    <div data-testid="billing-tab">
      <div className="billing-subtabs">
        {SUBTABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              className={`billing-subtab ${sub === t.id ? 'active' : ''}`}
              onClick={() => setSub(t.id)}
              data-testid={`billing-subtab-${t.id}`}
            >
              <Icon size={12} style={{ marginRight: 6, verticalAlign: '-1px' }} />
              {t.label}
            </button>
          );
        })}
      </div>

      {error && <div className="admin-error" data-testid="billing-error"><AlertCircle size={14} /> {error}</div>}
      {loading && <p className="body-regular">Loading…</p>}

      {!loading && sub === 'dashboard' && (
        <DashboardView
          stats={stats}
          invoices={invoices}
          onView={setViewInvoice}
          onPay={setPayInvoice}
          onNew={() => setEditingInvoice({})}
        />
      )}

      {!loading && sub === 'invoices' && (
        <InvoicesView
          invoices={invoices}
          onCreate={() => setEditingInvoice({})}
          onEdit={setEditingInvoice}
          onView={setViewInvoice}
          onPay={setPayInvoice}
          onDelete={handleDeleteInvoice}
        />
      )}

      {!loading && sub === 'customers' && (
        <CustomersView
          customers={customers}
          invoices={invoices}
          onCreate={() => setEditingCustomer({})}
          onEdit={setEditingCustomer}
          onDelete={handleDeleteCustomer}
        />
      )}

      {editingInvoice && (
        <InvoiceFormModal
          initial={editingInvoice}
          customers={customers}
          onClose={() => setEditingInvoice(null)}
          onSaved={(_, isEdit) => {
            setEditingInvoice(null);
            onToast?.(isEdit ? 'Invoice updated' : 'Invoice created');
            refresh();
          }}
          onCustomerCreated={refresh}
        />
      )}

      {viewInvoice && (
        <InvoiceViewModal
          invoice={viewInvoice}
          onClose={() => setViewInvoice(null)}
          onPay={() => { setPayInvoice(viewInvoice); setViewInvoice(null); }}
        />
      )}

      {payInvoice && (
        <PaymentModal
          invoice={payInvoice}
          onClose={() => setPayInvoice(null)}
          onSaved={() => {
            setPayInvoice(null);
            onToast?.('Payment recorded');
            refresh();
          }}
        />
      )}

      {editingCustomer && (
        <CustomerFormModal
          initial={editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onSaved={(_, isEdit) => {
            setEditingCustomer(null);
            onToast?.(isEdit ? 'Customer updated' : 'Customer added');
            refresh();
          }}
        />
      )}
    </div>
  );
};

/* ---------------- Dashboard ---------------- */
const DashboardView = ({ stats, invoices, onView, onPay, onNew }) => {
  const recent = invoices.slice(0, 6);
  return (
    <>
      <div className="admin-section-actions" style={{ justifyContent: 'space-between' }}>
        <h2 className="heading-2" style={{ margin: 0 }}>Atelier Billing</h2>
        <button className="btn-primary" onClick={onNew} data-testid="billing-new-invoice-btn">
          <Plus size={14} /> New Invoice
        </button>
      </div>

      <div className="billing-stats-grid">
        <StatCard label="Total Collected" value={fmtINR(stats?.revenue)} note="Sum of all payments" tone="success" />
        <StatCard label="Outstanding" value={fmtINR(stats?.outstanding)} note="Pending + partial balances" tone="warn" />
        <StatCard label="Total Invoices" value={String(stats?.total_invoices || 0)} note={`${stats?.fully_paid || 0} fully paid`} />
        <StatCard label="Customers" value={String(stats?.total_customers || 0)} note={`${stats?.partial || 0} partial · ${stats?.pending || 0} pending`} />
      </div>

      <div className="billing-section">
        <div className="billing-section-header">
          <h3 className="billing-section-title">Recent Invoices</h3>
        </div>
        {!recent.length ? (
          <p className="billing-empty">No invoices yet — create your first one.</p>
        ) : (
          <table className="billing-table" data-testid="billing-recent-table">
            <thead>
              <tr>
                <th>Invoice #</th><th>Customer</th><th>Date</th>
                <th className="num">Total</th><th className="num">Paid</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {recent.map((inv) => (
                <tr key={inv.id}>
                  <td><strong>{fmtInvoiceNumber(inv.number)}</strong></td>
                  <td>{inv.customer_name || <span className="muted">No name</span>}</td>
                  <td>{fmtDate(inv.date)}</td>
                  <td className="num">{fmtINR(inv.total)}</td>
                  <td className="num">{fmtINR(inv.paid_amount)}</td>
                  <td><span className={`billing-badge ${inv.status}`}>{STATUS_LABEL[inv.status]}</span></td>
                  <td>
                    <div className="billing-row-actions">
                      <button className="billing-row-btn" onClick={() => onView(inv)} title="View"><Eye size={13} /></button>
                      {inv.status !== 'paid' && (
                        <button className="billing-row-btn" onClick={() => onPay(inv)} title="Record payment"><Wallet size={13} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

const StatCard = ({ label, value, note, tone }) => (
  <div className="billing-stat-card">
    <p className="billing-stat-label">{label}</p>
    <p className="billing-stat-value">{value}</p>
    {note && <p className={`billing-stat-note ${tone || ''}`}>{note}</p>}
  </div>
);

/* ---------------- Invoices view ---------------- */
const InvoicesView = ({ invoices, onCreate, onEdit, onView, onPay, onDelete }) => {
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
      if (!term) return true;
      return (
        String(inv.number).includes(term)
        || (inv.customer_name || '').toLowerCase().includes(term)
        || (inv.customer_phone || '').toLowerCase().includes(term)
      );
    });
  }, [invoices, q, statusFilter]);

  return (
    <div className="billing-section">
      <div className="billing-section-header">
        <h3 className="billing-section-title">All Invoices</h3>
        <div className="billing-section-toolbar">
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input
              className="billing-search"
              style={{ paddingLeft: 30 }}
              placeholder="Search by # or customer"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="billing-invoices-search"
            />
          </div>
          <select
            className="billing-search"
            style={{ minWidth: 140 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="billing-invoices-status"
          >
            <option value="all">All statuses</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="pending">Pending</option>
          </select>
          <button className="btn-primary" onClick={onCreate} data-testid="billing-new-invoice-btn-2">
            <Plus size={14} /> New Invoice
          </button>
        </div>
      </div>

      {!filtered.length ? (
        <p className="billing-empty">No invoices matching the filters.</p>
      ) : (
        <table className="billing-table" data-testid="billing-invoices-table">
          <thead>
            <tr>
              <th>Invoice #</th><th>Customer</th><th>Date</th>
              <th className="num">Total</th><th className="num">Paid</th><th className="num">Balance</th>
              <th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((inv) => (
              <tr key={inv.id} data-testid={`billing-invoice-row-${inv.id}`}>
                <td><strong>{fmtInvoiceNumber(inv.number)}</strong></td>
                <td>{inv.customer_name || <span className="muted">No name</span>}</td>
                <td>{fmtDate(inv.date)}</td>
                <td className="num">{fmtINR(inv.total)}</td>
                <td className="num">{fmtINR(inv.paid_amount)}</td>
                <td className="num">{fmtINR((inv.total || 0) - (inv.paid_amount || 0))}</td>
                <td><span className={`billing-badge ${inv.status}`}>{STATUS_LABEL[inv.status]}</span></td>
                <td>
                  <div className="billing-row-actions">
                    <button className="billing-row-btn" onClick={() => onView(inv)} title="View"><Eye size={13} /></button>
                    <button className="billing-row-btn" onClick={() => onEdit(inv)} title="Edit"><Pencil size={13} /></button>
                    {inv.status !== 'paid' && (
                      <button className="billing-row-btn" onClick={() => onPay(inv)} title="Record payment"><Wallet size={13} /></button>
                    )}
                    <button className="billing-row-btn danger" onClick={() => onDelete(inv)} title="Delete" data-testid={`billing-invoice-delete-${inv.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ---------------- Customers view ---------------- */
const CustomersView = ({ customers, invoices, onCreate, onEdit, onDelete }) => {
  const summary = useMemo(() => {
    const map = {};
    for (const inv of invoices) {
      const key = inv.customer_id || inv.customer_name || 'unknown';
      if (!map[key]) map[key] = { count: 0, billed: 0, collected: 0 };
      map[key].count += 1;
      map[key].billed += Number(inv.total || 0);
      map[key].collected += Number(inv.paid_amount || 0);
    }
    return map;
  }, [invoices]);

  return (
    <div className="billing-section">
      <div className="billing-section-header">
        <h3 className="billing-section-title">Customers</h3>
        <button className="btn-primary" onClick={onCreate} data-testid="billing-new-customer-btn">
          <Plus size={14} /> Add Customer
        </button>
      </div>
      {!customers.length ? (
        <p className="billing-empty">No customers yet — add one to start invoicing.</p>
      ) : (
        <table className="billing-table" data-testid="billing-customers-table">
          <thead>
            <tr>
              <th>Name</th><th>Phone</th><th>City</th>
              <th className="num">Invoices</th><th className="num">Billed</th><th className="num">Collected</th><th className="num">Balance</th><th></th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => {
              const s = summary[c.id] || { count: 0, billed: 0, collected: 0 };
              const balance = s.billed - s.collected;
              return (
                <tr key={c.id} data-testid={`billing-customer-row-${c.id}`}>
                  <td><strong>{c.name}</strong></td>
                  <td>{c.phone || <span className="muted">—</span>}</td>
                  <td>{c.city || <span className="muted">—</span>}</td>
                  <td className="num">{s.count}</td>
                  <td className="num">{fmtINR(s.billed)}</td>
                  <td className="num">{fmtINR(s.collected)}</td>
                  <td className="num">{fmtINR(balance)}</td>
                  <td>
                    <div className="billing-row-actions">
                      <button className="billing-row-btn" onClick={() => onEdit(c)} title="Edit"><Pencil size={13} /></button>
                      <button className="billing-row-btn danger" onClick={() => onDelete(c)} title="Remove"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ---------------- Customer Form Modal ---------------- */
const CustomerFormModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    name: initial.name || '',
    phone: initial.phone || '',
    city: initial.city || '',
    address: initial.address || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    setErr(''); setSubmitting(true);
    try {
      if (isEdit) await adminApi.updateBillingCustomer(initial.id, form);
      else await adminApi.createBillingCustomer(form);
      onSaved(form, isEdit);
    } catch (e2) { setErr(formatApiError(e2)); } finally { setSubmitting(false); }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal billing-modal" onClick={(e) => e.stopPropagation()} data-testid="billing-customer-modal">
        <button className="admin-modal-close" onClick={onClose}><X size={20} /></button>
        <h2 className="heading-2" style={{ marginBottom: 24 }}>{isEdit ? 'Edit Customer' : 'Add Customer'}</h2>
        <form onSubmit={submit}>
          <div className="billing-form-grid">
            <div className="billing-field"><label>Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="customer-input-name" />
            </div>
            <div className="billing-field"><label>Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-input-phone" />
            </div>
            <div className="billing-field"><label>City</label>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="customer-input-city" />
            </div>
            <div className="billing-field billing-field-full"><label>Address</label>
              <textarea rows="3" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="customer-input-address" />
            </div>
          </div>
          {err && <div className="admin-error"><AlertCircle size={14} /> {err}</div>}
          <div className="admin-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting} data-testid="customer-submit">
              {submitting ? 'Saving…' : isEdit ? 'Save' : 'Add Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ---------------- Invoice Form Modal ---------------- */
const InvoiceFormModal = ({ initial, customers, onClose, onSaved, onCustomerCreated }) => {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    customer_id: initial.customer_id || '',
    customer_name: initial.customer_name || '',
    customer_phone: initial.customer_phone || '',
    customer_city: initial.customer_city || '',
    customer_address: initial.customer_address || '',
    date: initial.date || todayISO(),
    due: initial.due || dueDefaultISO(7),
    notes: initial.notes || '',
    gst_pct: initial.gst_pct ?? 0,
    items: (initial.items && initial.items.length ? initial.items : [{ desc: '', qty: 1, rate: 0, amount: 0 }]),
    initial_payment: 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [showAddCust, setShowAddCust] = useState(false);

  const subtotal = form.items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);
  const gstAmount = subtotal * (Number(form.gst_pct) || 0) / 100;
  const total = subtotal + gstAmount;

  const updateItem = (idx, key, val) => {
    setForm((f) => {
      const items = f.items.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, [key]: key === 'desc' ? val : Number(val) };
        next.amount = (Number(next.qty) || 0) * (Number(next.rate) || 0);
        return next;
      });
      return { ...f, items };
    });
  };
  const addRow = () => setForm((f) => ({ ...f, items: [...f.items, { desc: '', qty: 1, rate: 0, amount: 0 }] }));
  const delRow = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const handleCustomerSelect = (id) => {
    const c = customers.find((x) => x.id === id);
    if (!c) {
      setForm((f) => ({ ...f, customer_id: '', customer_name: '', customer_phone: '', customer_city: '', customer_address: '' }));
      return;
    }
    setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name, customer_phone: c.phone, customer_city: c.city, customer_address: c.address }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) { setErr('Customer is required.'); return; }
    if (!form.items.length || form.items.every((i) => !i.desc.trim() && !i.rate)) {
      setErr('Add at least one line item.'); return;
    }
    setErr(''); setSubmitting(true);
    try {
      const payload = {
        customer_id: form.customer_id || null,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_city: form.customer_city,
        customer_address: form.customer_address,
        date: form.date,
        due: form.due,
        notes: form.notes,
        gst_pct: Number(form.gst_pct) || 0,
        items: form.items.filter((i) => i.desc.trim() || i.rate || i.qty),
      };
      if (!isEdit && Number(form.initial_payment) > 0) {
        payload.initial_payment = { amount: Number(form.initial_payment), date: form.date, mode: 'Cash', ref: 'Initial' };
      }
      if (isEdit) await adminApi.updateInvoice(initial.id, payload);
      else await adminApi.createInvoice(payload);
      onSaved(payload, isEdit);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal billing-modal" onClick={(e) => e.stopPropagation()} data-testid="billing-invoice-modal">
        <button className="admin-modal-close" onClick={onClose}><X size={20} /></button>
        <h2 className="heading-2" style={{ marginBottom: 24 }}>{isEdit ? `Edit Invoice ${fmtInvoiceNumber(initial.number)}` : 'New Invoice'}</h2>

        <form onSubmit={submit}>
          <div className="billing-form-grid">
            <div className="billing-field">
              <label>Customer *</label>
              <select value={form.customer_id || ''} onChange={(e) => handleCustomerSelect(e.target.value)} data-testid="invoice-input-customer">
                <option value="">— Select / Walk-in —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ''}</option>)}
              </select>
              <button type="button" onClick={() => setShowAddCust(true)} className="admin-inline-btn" style={{ alignSelf: 'flex-start', marginTop: 4 }}>
                <Plus size={11} /> Add new customer
              </button>
            </div>
            <div className="billing-field">
              <label>Customer Name *</label>
              <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value, customer_id: '' })} required data-testid="invoice-input-cname" />
            </div>
            <div className="billing-field">
              <label>Phone</label>
              <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
            </div>
            <div className="billing-field">
              <label>City</label>
              <input value={form.customer_city} onChange={(e) => setForm({ ...form, customer_city: e.target.value })} />
            </div>
            <div className="billing-field">
              <label>Invoice Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="invoice-input-date" />
            </div>
            <div className="billing-field">
              <label>Due Date</label>
              <input type="date" value={form.due} onChange={(e) => setForm({ ...form, due: e.target.value })} />
            </div>
            <div className="billing-field">
              <label>GST %</label>
              <input type="number" min="0" max="100" step="0.01" value={form.gst_pct} onChange={(e) => setForm({ ...form, gst_pct: e.target.value })} data-testid="invoice-input-gst" />
            </div>
            <div className="billing-field billing-field-full">
              <label>Notes / Instructions</label>
              <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          <div className="billing-items" data-testid="invoice-items">
            <div className="billing-items-row head">
              <div>Description</div><div>Qty</div><div>Rate (₹)</div><div>Amount</div><div></div>
            </div>
            {form.items.map((it, idx) => (
              <div key={idx} className="billing-items-row">
                <input value={it.desc} onChange={(e) => updateItem(idx, 'desc', e.target.value)} placeholder="Bridal Lehenga" data-testid={`invoice-item-desc-${idx}`} />
                <input className="num" type="number" min="0" step="1" value={it.qty} onChange={(e) => updateItem(idx, 'qty', e.target.value)} />
                <input className="num" type="number" min="0" step="100" value={it.rate} onChange={(e) => updateItem(idx, 'rate', e.target.value)} />
                <div className="amount">{fmtINR((Number(it.qty) || 0) * (Number(it.rate) || 0))}</div>
                <button type="button" className="row-del" onClick={() => delRow(idx)} title="Remove"><X size={14} /></button>
              </div>
            ))}
          </div>
          <button type="button" className="billing-add-row" onClick={addRow} data-testid="invoice-add-row">+ Add row</button>

          <div className="billing-totals" style={{ marginTop: 18 }}>
            <div className="billing-totals-row"><span>Subtotal</span><span>{fmtINR(subtotal)}</span></div>
            <div className="billing-totals-row"><span>GST ({Number(form.gst_pct) || 0}%)</span><span>{fmtINR(gstAmount)}</span></div>
            <div className="billing-totals-row grand"><span>Grand Total</span><span>{fmtINR(total)}</span></div>
          </div>

          {!isEdit && (
            <div className="billing-form-grid" style={{ marginTop: 0 }}>
              <div className="billing-field">
                <label>Initial Payment Received (₹)</label>
                <input type="number" min="0" step="100" value={form.initial_payment} onChange={(e) => setForm({ ...form, initial_payment: e.target.value })} data-testid="invoice-initial-payment" />
                <span className="admin-field-hint" style={{ fontSize: 11 }}>Optional. If set, the invoice will be created in <em>Partial</em> or <em>Paid</em> state.</span>
              </div>
            </div>
          )}

          {err && <div className="admin-error"><AlertCircle size={14} /> {err}</div>}

          <div className="admin-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting} data-testid="invoice-submit">
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Invoice'}
            </button>
          </div>
        </form>

        {showAddCust && (
          <CustomerFormModal
            initial={{}}
            onClose={() => setShowAddCust(false)}
            onSaved={(data) => {
              setShowAddCust(false);
              onCustomerCreated?.();
              setForm((f) => ({ ...f, customer_name: data.name, customer_phone: data.phone, customer_city: data.city, customer_address: data.address }));
            }}
          />
        )}
      </div>
    </div>
  );
};

/* ---------------- Invoice View / Print Modal ---------------- */
const InvoiceViewModal = ({ invoice: inv, onClose, onPay }) => {
  const balance = (inv.total || 0) - (inv.paid_amount || 0);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await exportInvoicePdf('invoice-print-area', `silk-sentiment-invoice-${fmtInvoiceNumber(inv.number).replace('#', '')}.pdf`);
    } catch (e) {
      window.alert('Could not generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal billing-modal" onClick={(e) => e.stopPropagation()} data-testid="billing-invoice-view">
        <button className="admin-modal-close" onClick={onClose}><X size={20} /></button>

        <div className="billing-print billing-print--compact" id="invoice-print-area" style={{ padding: '6px 4px 12px' }}>
          <div style={{ borderBottom: '1px solid #d8cfb8', paddingBottom: 10, marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              <h2 style={{ fontSize: 20 }}>Silk &amp; Sentiment</h2>
              <p style={{ fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#7d7565', margin: '3px 0 0' }}>Elegance in Every Thread</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: 17, fontFamily: 'Playfair Display, serif' }}>INVOICE</p>
              <p style={{ margin: '3px 0 0', fontSize: 11 }}>{fmtInvoiceNumber(inv.number)}</p>
              <p style={{ margin: '2px 0 0', fontSize: 10, color: '#7d7565' }}>{fmtDate(inv.date)}</p>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#7d7565', margin: 0 }}>Bill To</p>
              <p style={{ margin: '3px 0 0', fontWeight: 600, fontSize: 11 }}>{inv.customer_name}</p>
              {inv.customer_phone && <p style={{ margin: 0, fontSize: 10 }}>{inv.customer_phone}</p>}
              {inv.customer_city && <p style={{ margin: 0, fontSize: 10 }}>{inv.customer_city}</p>}
              {inv.customer_address && <p style={{ margin: 0, fontSize: 10, whiteSpace: 'pre-line' }}>{inv.customer_address}</p>}
            </div>
            <div style={{ textAlign: 'right', fontSize: 10 }}>
              <p style={{ margin: 0 }}>Due: <strong>{fmtDate(inv.due)}</strong></p>
              <p style={{ margin: '3px 0 0' }}>Status: <strong>{STATUS_LABEL[inv.status]}</strong></p>
            </div>
          </div>
          <table className="billing-table" style={{ marginBottom: 10 }}>
            <thead>
              <tr><th>Description</th><th className="num">Qty</th><th className="num">Rate</th><th className="num">Amount</th></tr>
            </thead>
            <tbody>
              {inv.items?.map((it, idx) => (
                <tr key={idx}>
                  <td>{it.desc || <span className="muted">—</span>}</td>
                  <td className="num">{it.qty}</td>
                  <td className="num">{fmtINR(it.rate)}</td>
                  <td className="num">{fmtINR(it.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="billing-totals">
            <div className="billing-totals-row"><span>Subtotal</span><span>{fmtINR(inv.subtotal)}</span></div>
            <div className="billing-totals-row"><span>GST ({inv.gst_pct}%)</span><span>{fmtINR(inv.gst_amount)}</span></div>
            <div className="billing-totals-row grand"><span>Grand Total</span><span>{fmtINR(inv.total)}</span></div>
            <div className="billing-totals-row"><span>Paid</span><span>{fmtINR(inv.paid_amount)}</span></div>
            <div className="billing-totals-row"><span>Balance</span><span>{fmtINR(balance)}</span></div>
          </div>
          {inv.notes && (
            <div style={{ marginTop: 12, padding: 10, background: '#fbfaf5', border: '1px solid #ecdfb8', fontSize: 10, fontStyle: 'italic' }}>
              <strong>Notes:</strong> {inv.notes}
            </div>
          )}
          {inv.payments?.length > 0 && (
            <>
              <p style={{ marginTop: 14, marginBottom: 6, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#7d7565' }}>Payment History</p>
              <table className="billing-table">
                <thead><tr><th>Date</th><th>Mode</th><th>Reference</th><th className="num">Amount</th></tr></thead>
                <tbody>
                  {inv.payments.map((p, idx) => (
                    <tr key={idx}>
                      <td>{fmtDate(p.date)}</td><td>{p.mode}</td><td>{p.ref || '—'}</td><td className="num">{fmtINR(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="admin-modal-actions">
          <button className="btn-secondary" onClick={handleDownload} disabled={downloading} data-testid="invoice-download-btn">
            <Download size={14} /> {downloading ? 'Generating…' : 'Download PDF'}
          </button>
          <button className="btn-secondary" onClick={() => window.print()} data-testid="invoice-print-btn">
            <Printer size={14} /> Print
          </button>
          {inv.status !== 'paid' && (
            <button className="btn-primary" onClick={onPay}>
              <Wallet size={14} /> Record Payment
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------------- Payment Modal ---------------- */
const PaymentModal = ({ invoice: inv, onClose, onSaved }) => {
  const balance = (inv.total || 0) - (inv.paid_amount || 0);
  const [form, setForm] = useState({ amount: balance, date: todayISO(), mode: 'Cash', ref: '' });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [payments, setPayments] = useState(inv.payments || []);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { setErr('Enter a valid payment amount.'); return; }
    setErr(''); setSubmitting(true);
    try {
      const updated = await adminApi.recordPayment(inv.id, { amount: amt, date: form.date, mode: form.mode, ref: form.ref });
      setPayments(updated.payments || []);
      onSaved();
    } catch (e2) { setErr(formatApiError(e2)); } finally { setSubmitting(false); }
  };

  const handleDeletePayment = async (idx) => {
    if (!window.confirm('Remove this payment?')) return;
    try {
      const updated = await adminApi.deletePayment(inv.id, idx);
      setPayments(updated.payments || []);
      onSaved();
    } catch (e2) { setErr(formatApiError(e2)); }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal billing-modal" onClick={(e) => e.stopPropagation()} data-testid="billing-payment-modal">
        <button className="admin-modal-close" onClick={onClose}><X size={20} /></button>
        <h2 className="heading-2" style={{ marginBottom: 18 }}>Record Payment</h2>

        <div className="billing-cust-card">
          <div><span>Invoice</span><br /><strong>{fmtInvoiceNumber(inv.number)}</strong></div>
          <div><span>Customer</span><br /><strong>{inv.customer_name}</strong></div>
          <div><span>Total</span><br /><strong>{fmtINR(inv.total)}</strong></div>
          <div><span>Paid</span><br /><strong>{fmtINR(inv.paid_amount)}</strong></div>
          <div><span>Balance</span><br /><strong style={{ color: balance > 0 ? '#a85a2c' : '#5a7536' }}>{fmtINR(balance)}</strong></div>
        </div>

        {payments.length > 0 && (
          <div className="billing-payments" data-testid="billing-payment-history">
            {payments.map((p, idx) => (
              <div className="billing-payment-row" key={idx}>
                <div className="left">
                  <strong>{fmtINR(p.amount)} · {p.mode}</strong>
                  <small>{fmtDate(p.date)}{p.ref ? ` · ${p.ref}` : ''}</small>
                </div>
                <button className="billing-row-btn danger" onClick={() => handleDeletePayment(idx)} title="Remove"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={submit}>
          <div className="billing-form-grid">
            <div className="billing-field"><label>Amount (₹) *</label>
              <input type="number" min="0" step="100" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="payment-input-amount" />
            </div>
            <div className="billing-field"><label>Payment Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="billing-field"><label>Mode</label>
              <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="payment-input-mode">
                {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="billing-field"><label>Reference / Note</label>
              <input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} placeholder="UPI txn ID, cheque #, etc." />
            </div>
          </div>
          {err && <div className="admin-error"><AlertCircle size={14} /> {err}</div>}
          <div className="admin-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
            <button type="submit" className="btn-primary" disabled={submitting} data-testid="payment-submit">
              {submitting ? 'Saving…' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillingTab;
